import { useRef, useEffect } from 'react';

// ─── Light direction (upper-left, slightly toward viewer) ─────────────────────
const LX = -0.42, LY = -0.20, LZ = 0.88;

// ─── Module-level sphere map cache (built once, reused across all planets) ────
let _sphereMapCache = null;
let _sphereMapSize  = 0;

function getSphereMap(size) {
  if (_sphereMapCache && _sphereMapSize === size) return _sphereMapCache;
  _sphereMapSize = size;

  const R   = size / 2;
  const map = new Float32Array(size * size * 3);

  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const idx = (y * size + x) * 3;
      const u   = (x - R) / R;
      const v   = (y - R) / R;
      const r2  = u * u + v * v;

      if (r2 >= 1.0) { map[idx] = -1; continue; }

      const z     = Math.sqrt(1.0 - r2);
      // Longitude (0–1) and latitude (0–1)
      map[idx]     = Math.atan2(u, z) / Math.PI + 0.5;
      map[idx + 1] = Math.asin(Math.max(-1, Math.min(1, v))) / Math.PI + 0.5;
      // Pre-bake Lambertian + limb darkening into brightness
      const dot   = u * LX + v * LY + z * LZ;
      const light = Math.max(0, Math.min(1, dot * 2.8 + 0.14));
      const limb  = 0.52 + 0.48 * z;
      map[idx + 2] = limb * (0.08 + 0.92 * light);
    }
  }

  return (_sphereMapCache = map);
}

// ─── Component ────────────────────────────────────────────────────────────────
export default function PlanetStage({ planet, tex, isOn }) {
  const canvasRef = useRef(null);
  const rafRef    = useRef(null);

  // isOnRef is updated inline (during render) so the RAF loop always sees
  // the latest value without any async useEffect delay.
  const isOnRef   = useRef(isOn);
  isOnRef.current = isOn;

  // All mutable render state lives in a ref — no state updates, no re-renders.
  const s = useRef({
    ready          : false,
    loadedUrl      : null,
    texData        : null,
    texW           : 0,
    texH           : 0,
    speedMs        : 30000,
    rot            : 0,
    lastTime       : null,
    hasInitialFrame: false, // true once at least one frame has been drawn
  }).current;

  // ── Effect 1: canvas setup + permanent RAF loop (mount / unmount only) ──────
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // Size the canvas to match the actual CSS display size × devicePixelRatio
    // (getBoundingClientRect works for opacity:0 elements since layout still runs)
    const dpr     = Math.min(window.devicePixelRatio || 1, 2);
    const cssSize = canvas.getBoundingClientRect().width || 320;
    const size    = Math.min(Math.round(cssSize * dpr), 700);

    canvas.width  = size;
    canvas.height = size;

    const ctx       = canvas.getContext('2d', { alpha: true });
    const sphereMap = getSphereMap(size);
    const imgData   = ctx.createImageData(size, size);
    const out       = imgData.data;
    const total     = size * size;

    function render(t) {
      rafRef.current = requestAnimationFrame(render);

      // Wait until texture is decoded
      if (!s.ready) return;

      // Inactive planet: render one initial frame then pause to save CPU.
      // Reset lastTime so re-activation never causes a rotation jump.
      if (!isOnRef.current) {
        if (s.hasInitialFrame) {
          s.lastTime = null;
          return;
        }
        // Fall through to render the one initial frame (so canvas isn't blank
        // for the first 16ms after the planet becomes active)
      }

      // ── Advance rotation ─────────────────────────────────────────────────
      if (!s.lastTime) s.lastTime = t;
      const dt    = t - s.lastTime;
      s.lastTime  = t;
      s.rot      += dt / s.speedMs;
      const rot   = s.rot % 1;

      // ── Pixel-by-pixel sphere projection ─────────────────────────────────
      const { texData, texW, texH } = s;
      out.fill(0);

      for (let i = 0; i < total; i++) {
        const mi  = i * 3;
        const phi = sphereMap[mi];
        if (phi < 0) continue;                          // outside sphere

        const srcX = (((phi + rot) % 1) * texW) | 0;   // texture column
        const srcY = (sphereMap[mi + 1] * texH) | 0;   // texture row
        const ti   = (srcY * texW + srcX) * 4;
        const br   = sphereMap[mi + 2];                 // pre-baked brightness
        const pi   = i * 4;

        out[pi]     = (texData[ti]     * br) | 0;
        out[pi + 1] = (texData[ti + 1] * br) | 0;
        out[pi + 2] = (texData[ti + 2] * br) | 0;
        out[pi + 3] = 255;
      }

      ctx.putImageData(imgData, 0, 0);
      s.hasInitialFrame = true;
    }

    rafRef.current = requestAnimationFrame(render);
    return () => cancelAnimationFrame(rafRef.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // ← mount/unmount only — never restarts on navigation

  // ── Effect 2: texture loader (only when URL actually changes) ───────────────
  useEffect(() => {
    const url = tex?.url;
    if (!url || s.loadedUrl === url) return; // same texture — nothing to do

    s.loadedUrl      = url;
    s.speedMs        = parseFloat(planet.speed || '30') * 1000;
    s.ready          = false;
    s.lastTime       = null;
    s.hasInitialFrame = false;

    const img = new Image();
    img.onload = () => {
      const off    = document.createElement('canvas');
      off.width    = img.width;
      off.height   = img.height;
      const offCtx = off.getContext('2d');
      offCtx.drawImage(img, 0, 0);
      s.texData = offCtx.getImageData(0, 0, img.width, img.height).data;
      s.texW    = img.width;
      s.texH    = img.height;
      s.ready   = true;
    };
    img.src = url;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tex?.url, planet.speed]); // ← URL change only, not object reference

  return (
    <div className={`pw${isOn ? ' on' : ''}`} style={{ '--glow': planet.glow }}>
      <canvas
        ref={canvasRef}
        className="planet"
        style={{ boxShadow: planet.bs }}
      />
    </div>
  );
}
